import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Tag,
  Warehouse,
  Users,
  Wrench,
  Headphones,
  Truck,
  Star,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import {
  HERO_IMG,
  BRANDS,
  CATEGORIES,
  WHY_CHOOSE,
  STATS,
  COMPANY,
} from "../data/site";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { Counter } from "../components/Counter";

const IconMap = {
  ShieldCheck,
  Tag,
  Warehouse,
  Users,
  Wrench,
  Headphones,
  Truck,
  Star,
};

const Home = () => {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative w-full min-h-[92vh] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/75 to-[#111111]/40" />
        <div className="absolute inset-0 bg-[#111111]/30" />

        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32 w-full">
          <div className="max-w-3xl">
            <Reveal>
              <div className="inline-flex items-center bg-white px-3 py-1.5 rounded-sm mb-6">
                <img
                  src="/brand/rns-logo-wide.png"
                  alt="RNS Automobile"
                  className="h-16 md:h-20 w-auto object-contain"
                />
              </div>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-12 bg-[#C8102E]" />
                <span className="text-xs uppercase tracking-overline font-bold text-[#A7A9AC]">
                  Est. Berhampore · West Bengal
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-display font-black uppercase text-white tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
                Trusted Automobile{" "}
                <span className="text-[#C8102E]">Spare Parts</span> Supplier
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 text-base md:text-lg text-[#D4D4D8] max-w-2xl leading-relaxed">
                Specializing in Mahindra, Maruti Suzuki &amp; Tata spare parts —
                with quality, precision and reliability you can trust across
                every workshop, fleet and vehicle.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/products"
                  data-testid="hero-cta-products"
                  className="group inline-flex items-center gap-2 h-12 px-6 bg-[#C8102E] text-white text-sm font-bold uppercase tracking-overline hover:bg-[#A00D24] transition-colors duration-200"
                >
                  Explore Products
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-contact"
                  className="inline-flex items-center gap-2 h-12 px-6 border border-white/40 text-white text-sm font-bold uppercase tracking-overline hover:border-white hover:bg-white/10 transition-colors duration-200"
                >
                  Contact Us
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Brand strip */}
          <div className="absolute left-5 sm:left-8 right-5 sm:right-8 bottom-8">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/15 pt-6">
              <span className="text-[11px] uppercase tracking-overline text-[#A7A9AC]">
                Specialisation
              </span>
              {BRANDS.map((b) => (
                <span
                  key={b.key}
                  className="text-sm md:text-base font-display font-bold text-white/90"
                >
                  {b.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section
        className="py-20 lg:py-28 bg-background"
        data-testid="why-choose-section"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-10 bg-[#C8102E]" />
                  <span className="text-xs uppercase tracking-overline font-bold text-muted-foreground">
                    Why Choose Us
                  </span>
                </div>
                <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">
                  Precision parts. <br />
                  <span className="text-[#C8102E]">Uncompromising trust.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.1}>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                  For over a decade, mechanics, workshops and fleet owners
                  across West Bengal have relied on RNS Automobiles for
                  dependable spare parts and honest guidance. Here's what makes
                  the difference.
                </p>
              </Reveal>
            </div>
          </div>

          <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 border-t border-l border-border">
            {WHY_CHOOSE.map((w) => {
              const Icon = IconMap[w.icon] || CheckCircle2;
              return (
                <StaggerItem
                  key={w.title}
                  className="border-r border-b border-border p-6 lg:p-8 group hover:bg-card transition-colors duration-300"
                >
                  <div className="h-11 w-11 flex items-center justify-center border border-border group-hover:border-[#C8102E] group-hover:text-[#C8102E] transition-colors duration-300">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 font-display font-bold text-base md:text-lg leading-snug">
                    {w.title}
                  </h3>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* FEATURED BRANDS */}
      <section
        className="relative py-20 lg:py-28 bg-[#111111] text-white overflow-hidden"
        data-testid="brands-section"
      >
        <div className="absolute inset-0 opacity-[0.06] noise-overlay pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#C8102E]" />
              <span className="text-xs uppercase tracking-overline font-bold text-[#A7A9AC]">
                Featured Brands
              </span>
            </div>
            <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter max-w-3xl">
              Specialised for India&apos;s{" "}
              <span className="text-[#C8102E]">most driven</span> vehicles.
            </h2>
          </Reveal>

          <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-white/10">
            {BRANDS.map((b, i) => (
              <StaggerItem
                key={b.key}
                className="border-r border-b border-white/10 p-8 lg:p-10 group hover:bg-white/[0.03] transition-colors duration-300"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-black text-4xl md:text-5xl tracking-tighter">
                    0{i + 1}
                  </span>
                  <span className="h-px flex-1 bg-white/15" />
                </div>
                <h3 className="mt-8 font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">
                  {b.name}
                </h3>
                <p className="mt-3 text-sm text-[#A7A9AC] uppercase tracking-overline">
                  {b.tagline}
                </p>
                <p className="mt-6 text-sm text-white/70 leading-relaxed">
                  {b.description}
                </p>
                <Link
                  to="/brands"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-overline text-[#C8102E] group-hover:gap-3 transition-all duration-200"
                >
                  Explore <ChevronRight className="h-4 w-4" />
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <p className="mt-8 text-sm text-[#A7A9AC] italic">
            Genuine &amp; high-quality aftermarket components available.
          </p>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section
        className="py-20 lg:py-28 bg-background"
        data-testid="categories-section"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal>
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <span className="h-px w-10 bg-[#C8102E]" />
                  <span className="text-xs uppercase tracking-overline font-bold text-muted-foreground">
                    Product Categories
                  </span>
                </div>
                <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter">
                  The full spectrum <br /> of automotive components.
                </h2>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-overline text-[#C8102E] hover:gap-3 transition-all duration-200"
                data-testid="view-all-categories"
              >
                View All Categories <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-t border-l border-border">
            {CATEGORIES.slice(0, 8).map((c) => (
              <StaggerItem
                key={c.key}
                className="group border-r border-b border-border relative overflow-hidden bg-card"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {c.name}
                  </h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                    {c.description}
                  </p>
                  <Link
                    to={`/contact?part=${encodeURIComponent(c.name)}`}
                    data-testid={`enquire-${c.key}`}
                    className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-overline text-[#C8102E] hover:gap-3 transition-all duration-200"
                  >
                    Enquire Now <ChevronRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* OUR COMMITMENT */}
      <section
        className="py-20 lg:py-28 bg-card"
        data-testid="commitment-section"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#C8102E]" />
                <span className="text-xs uppercase tracking-overline font-bold text-muted-foreground">
                  Our Commitment
                </span>
              </div>
              <h2 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tighter leading-[1.02]">
                Quality parts. <br />
                Honest guidance. <br />
                <span className="text-[#C8102E]">Long-term trust.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                At RNS Automobiles, we believe every customer deserves quality
                products, honest guidance, competitive pricing, and dependable
                service. Our commitment is to help keep your vehicles running
                safely and efficiently by supplying reliable spare parts
                sourced from trusted manufacturers.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                We strive to build long-term relationships through
                professionalism, integrity, and exceptional customer care —
                whether you run a single vehicle or a fleet of hundreds.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  "Sourced from trusted manufacturers",
                  "Honest, expert product guidance",
                  "Competitive workshop pricing",
                  "Dependable long-term supply",
                ].map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-sm md:text-base"
                  >
                    <CheckCircle2 className="h-5 w-5 mt-0.5 text-[#C8102E] shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section
        className="py-20 lg:py-24 bg-[#111111] text-white"
        data-testid="stats-section"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="border-r border-b border-white/10 p-8 lg:p-10"
              >
                <div className="font-display font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter text-[#C8102E]">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <p className="mt-3 text-xs uppercase tracking-overline text-[#A7A9AC]">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-20 lg:py-24 bg-[#C8102E] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 noise-overlay pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-display font-black uppercase text-3xl md:text-5xl tracking-tighter leading-[1.05]">
              Need a specific spare part? <br />
              <span className="opacity-80">
                Our team is one message away.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 h-12 px-6 bg-white text-[#111111] text-sm font-bold uppercase tracking-overline hover:bg-[#111111] hover:text-white transition-colors duration-200"
              data-testid="cta-enquire-now"
            >
              Enquire Now <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneLink}`}
              className="inline-flex items-center gap-2 h-12 px-6 border border-white/70 text-white text-sm font-bold uppercase tracking-overline hover:bg-white/10 transition-colors duration-200"
            >
              Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
